import { useSyncRef } from '@react-aria/utils';
import * as React from 'react';
import {
    type AriaOverlayProps,
    FocusScope,
    type FocusScopeProps,
    useObjectRef,
    useOverlay,
} from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { AnimatedStateContext } from '../animations';
import { checkIsOverlayOpen } from '../util';
import { useContextMenuOutside } from '../util/useContextMenuOutside';
import { OverlayContext } from './OverlayContext';
import { getOverlayTriggerProps, OverlayTriggerContext } from './OverlayTrigger';

export const OverlayFocusContainedContext = React.createContext<boolean>(true);

export type OverlayProps = React.ComponentPropsWithoutRef<'div'> &
    Pick<FocusScopeProps, 'autoFocus' | 'contain' | 'restoreFocus'> &
    Pick<AriaOverlayProps, 'isDismissable'>;

/**
 * A container which renders an overlay such as a popover or modal in a portal,
 * and provides a focus scope for the child elements.
 */
export const Overlay = React.forwardRef(function Overlay(
    props: OverlayProps,
    forwardedRef: React.ForwardedRef<HTMLDivElement>
) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    const trigger = React.useContext(OverlayTriggerContext);
    assert(
        context !== null && animatedState !== null && trigger !== null,
        'Overlay must be used within a OverlayRoot'
    );

    const {
        autoFocus = true,
        contain = true,
        restoreFocus = true,
        isDismissable: isDismissableProp = true,
        ...divProps
    } = props;

    const isExiting = animatedState === 'exiting';
    const isExited = animatedState === 'exited';

    const ref = useObjectRef<HTMLDivElement>(forwardedRef);
    useSyncRef(context, ref);

    const { isOverlayInDialogOpened } = useInteractOutside(ref);

    const isFocusContained =
        React.useContext(OverlayFocusContainedContext) &&
        !isExiting &&
        !isExited &&
        !isOverlayInDialogOpened &&
        contain;

    const isClosable = context.isClosable && !context.isPending;
    const isDismissable = isClosable && isDismissableProp;
    const isOpen = !isExiting && !isExited;

    const { overlayProps } = useOverlay(
        {
            isDismissable,
            onClose: trigger.close,
            isOpen,
            shouldCloseOnBlur: false,
            shouldCloseOnInteractOutside: (element) => {
                // Do not close if the element is a palette overlay.
                if (element instanceof HTMLElement && element.dataset.paletteOverlay === 'true') {
                    return false;
                }

                if (element instanceof Element) {
                    // Do not close if the element interacted with is an overlay in the dialog (e.g palette, feedback button...).
                    if (isOverlayInDialogOpened) {
                        return false;
                    }
                }
                return isClosable;
            },
            isKeyboardDismissDisabled: !isClosable,
        },
        ref
    );

    useContextMenuOutside({
        ref,
        onInteractOutside: () => {
            trigger.close();
        },
        isDisabled: !isOpen || !isDismissable,
    });

    const triggerProps = getOverlayTriggerProps(trigger, divProps);

    const content = (
        <div
            ref={ref}
            data-spine-overlay="true"
            tabIndex={-1}
            {...overlayProps}
            {...divProps}
            {...triggerProps}
        />
    );

    // For hover, there is no focus scope.
    if (trigger.action === 'hover') {
        return content;
    }

    return (
        <FocusScope contain={isFocusContained} restoreFocus={restoreFocus} autoFocus={autoFocus}>
            <div
                ref={ref}
                data-spine-overlay="true"
                tabIndex={-1}
                {...overlayProps}
                {...divProps}
                {...triggerProps}
            />
        </FocusScope>
    );
});

function useInteractOutside(ref: React.RefObject<HTMLElement>) {
    const [isOverlayInDialogOpened, setOverlayInDialogOpened] = React.useState(false);

    React.useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Element) || !ref.current) {
                return;
            }

            if (ref.current.contains(target)) {
                return;
            }

            const isOtherOverlayOpened = checkIsOverlayOpen({
                includeOverlayTypes: ['palette', 'popover', 'feedback', 'toast'],
            });
            setOverlayInDialogOpened(isOtherOverlayOpened);
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
        };
    }, [ref]);

    return {
        isOverlayInDialogOpened,
    };
}
